import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faStethoscope,
  faPills,
  faChartLine,
  faClipboardCheck,
  faCalculator,
} from "@fortawesome/free-solid-svg-icons";

// Import the proper MDT aid properties with constraints
import {
  getFieldsByCategory,
  FieldType,
  MDTAidProperProperties,
  getAllFieldNames,
} from "../../../src/types/mdtaid-proper-constraints.enum";

import { saveAssessment } from '../utils/assessmentHistory';
// Import MDTAid formulas for automatic calculation
import {
  MDTAidFormulas,
  PatientData,
  TreatmentRecommendations,
} from "../../../src/types/mdtaid-formulas";

// Import sub-components
import { CaseLogPanel } from "./assessment/CaseLogPanel";
import { ImagingPanel } from "./assessment/ImagingPanel";
import { CaseSummaryPanel } from "./assessment/CaseSummaryPanel";

interface FormData {
  [key: string]: string;
}

const initialFormData: FormData = {};

// Function to map form data to PatientData interface for formula calculations
const mapFormDataToPatientData = (formData: FormData): PatientData => {
  // Convert string values to appropriate types
  const parseNumber = (value: string): number => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  return {
    // Demographics
    age: parseNumber(formData["Age"] || "0"),
    menopausalStatus: formData["Menopausal Status"] || "Unknown",

    // Tumor characteristics
    setting: formData["Setting"] || "Adjuvant",
    surgery: formData["Surgery"] || "Unknown",
    invasiveTumorSize: parseNumber(formData["Invasive tumor size (mm)"] || "0"),
    grade: parseNumber(formData["Grade"] || "0"),
    lymphNodes: parseNumber(formData["Number of positive lymph nodes"] || "0"),
    erStatus: formData["ER status"] || "Unknown",
    her2Status: formData["HER2 status"] || "Unknown",
    npiScore: parseNumber(formData["NPI score"] || "0"),

    // Additional characteristics
    lvi: formData["Lymphovascular invasion"] || "Unknown",
    inflammatory: formData["Inflammatory breast cancer"] || "No",
    margins: formData["Surgical margins"] || "Unknown",
    prStatus: formData["PR status"] || "Unknown",
    wholeSize: parseNumber(formData["Whole tumor size (mm)"] || "0"),
  };
};

// Function to populate read-only output fields with formula results
const populateOutputFields = (
  formData: FormData,
  recommendations: TreatmentRecommendations,
  setFormData: React.Dispatch<React.SetStateAction<FormData>>,
): void => {
  const updates: FormData = { ...formData };

  // PREDICT Data fields (using object keys, not display names)
  if (recommendations.predictData) {
    const predictData = recommendations.predictData;

    // Map to the actual PREDICT field keys (not display names)
    if (predictData.chemotherapy) {
      const chemoValue = `${predictData.chemotherapy.additionalBenefit} → ${predictData.chemotherapy.overallSurvival}% survival`;
      updates["benefitFromChemotherapy"] = chemoValue;
    }

    if (predictData.bisphosphonates) {
      const bisphosValue = `${predictData.bisphosphonates.additionalBenefit} → ${predictData.bisphosphonates.overallSurvival}% survival`;
      updates["benefitFromBisphosphonates"] = bisphosValue;
    }

    if (predictData.hormoneTherapy) {
      const hormoneValue = `${predictData.hormoneTherapy.additionalBenefit} → ${predictData.hormoneTherapy.overallSurvival}% survival`;
      updates["benefitFromEndocrineTherapy"] = hormoneValue;
    }
  }

  // Treatment fields (using object keys, not display names)
  // Staging
  if (recommendations.staging && recommendations.staging.includes("required")) {
    updates["staging"] = "Staging Required";
  } else if (recommendations.staging) {
    updates["staging"] = "Not Required";
  } else {
    updates["staging"] = "No Recommendation";
  }

  // Chemotherapy
  if (recommendations.chemotherapy && recommendations.chemotherapy.length > 0) {
    const hasRecommendation = recommendations.chemotherapy.some(
      (rec) =>
        rec.toLowerCase().includes("recommend") ||
        rec.toLowerCase().includes("offer"),
    );
    const hasConsider = recommendations.chemotherapy.some(
      (rec) =>
        rec.toLowerCase().includes("consider") ||
        rec.toLowerCase().includes("oncotype"),
    );

    if (hasRecommendation) {
      updates["chemotherapy"] = "Recommended";
    } else if (hasConsider) {
      updates["chemotherapy"] = "Consider";
    } else {
      updates["chemotherapy"] = "Not recommended";
    }
  } else {
    updates["chemotherapy"] = "No Recommendation";
  }

  // Endocrine therapy
  if (
    recommendations.endocrineTherapy &&
    recommendations.endocrineTherapy.length > 0
  ) {
    const hasRecommendation = recommendations.endocrineTherapy.some(
      (rec) =>
        rec.toLowerCase().includes("offer") ||
        rec.toLowerCase().includes("recommend"),
    );

    if (hasRecommendation) {
      updates["endocrineTherapy"] = "Recommended";
    } else {
      updates["endocrineTherapy"] = "Consider";
    }
  } else {
    updates["endocrineTherapy"] = "No Recommendation";
  }

  // Radiotherapy
  if (recommendations.radiotherapy && recommendations.radiotherapy.length > 0) {
    const hasRecommendation = recommendations.radiotherapy.some(
      (rec) =>
        rec.toLowerCase().includes("adjuvant") ||
        rec.toLowerCase().includes("recommend"),
    );

    if (hasRecommendation) {
      updates["radiotherapy"] = "Recommended";
    } else {
      updates["radiotherapy"] = "Consider";
    }
  } else {
    updates["radiotherapy"] = "No Recommendation";
  }

  // Genetics
  if (
    recommendations.geneticTesting &&
    recommendations.geneticTesting.length > 0
  ) {
    const hasRefer = recommendations.geneticTesting.some(
      (rec) =>
        rec.toLowerCase().includes("refer") ||
        rec.toLowerCase().includes("genetic"),
    );

    if (hasRefer) {
      updates["genetics"] = "Refer";
    } else {
      updates["genetics"] = "Not required";
    }
  } else {
    updates["genetics"] = "No Recommendation";
  }

  // Bisphosphonates
  if (recommendations.predictData?.bisphosphonates) {
    updates["bisphosphonates"] = "Recommended";
  } else if (recommendations.predictData) {
    updates["bisphosphonates"] = "Not recommended";
  } else {
    updates["bisphosphonates"] = "No Recommendation";
  }

  // Duration - default to 5 years for endocrine therapy
  if (
    recommendations.endocrineTherapy &&
    recommendations.endocrineTherapy.length > 0
  ) {
    updates["duration"] = "5 years";
  } else {
    updates["duration"] = "No Recommendation";
  }

  // ECHO - check if trastuzumab or anthracyclines mentioned
  const needsEcho =
    (recommendations.chemotherapy &&
      recommendations.chemotherapy.some(
        (rec) =>
          rec.toLowerCase().includes("trastuzumab") ||
          rec.toLowerCase().includes("anthracycline"),
      )) ||
    (recommendations.additionalTherapies &&
      recommendations.additionalTherapies.some((rec) =>
        rec.toLowerCase().includes("trastuzumab"),
      ));

  if (recommendations.chemotherapy || recommendations.additionalTherapies) {
    updates["echo"] = needsEcho ? "Required" : "Not required";
  } else {
    updates["echo"] = "No Recommendation";
  }

  // DEXA - for bisphosphonates
  if (recommendations.predictData?.bisphosphonates) {
    updates["dexa"] = "Required";
  } else if (recommendations.predictData) {
    updates["dexa"] = "Not required";
  } else {
    updates["dexa"] = "No Recommendation";
  }

  // Outcome fields (using object keys, not display names)
  const summaryParts = [];
  if (recommendations.staging)
    summaryParts.push(`Staging: ${recommendations.staging}`);
  if (recommendations.chemotherapy.length > 0) {
    summaryParts.push(
      `Chemotherapy: ${recommendations.chemotherapy.join(", ")}`,
    );
  }
  if (recommendations.endocrineTherapy.length > 0) {
    summaryParts.push(
      `Endocrine: ${recommendations.endocrineTherapy.join(", ")}`,
    );
  }
  if (recommendations.radiotherapy.length > 0) {
    summaryParts.push(
      `Radiotherapy: ${recommendations.radiotherapy.join(", ")}`,
    );
  }

  updates["caseSummary"] = summaryParts.join("\n\n");

  // Treatment suggestion with PREDICT data
  const treatmentParts = [];
  if (recommendations.predictData) {
    const pd = recommendations.predictData;
    treatmentParts.push(
      `PREDICT ${pd.yearsToConsider}-year survival analysis:`,
    );
    treatmentParts.push(`• Surgery only: ${pd.surgeryOnly.overallSurvival}%`);

    if (pd.hormoneTherapy) {
      treatmentParts.push(
        `• + Hormone therapy: ${pd.hormoneTherapy.additionalBenefit} → ${pd.hormoneTherapy.overallSurvival}%`,
      );
    }
    if (pd.chemotherapy) {
      treatmentParts.push(
        `• + Chemotherapy: ${pd.chemotherapy.additionalBenefit} → ${pd.chemotherapy.overallSurvival}%`,
      );
    }
    if (pd.trastuzumab) {
      treatmentParts.push(
        `• + Trastuzumab: ${pd.trastuzumab.additionalBenefit} → ${pd.trastuzumab.overallSurvival}%`,
      );
    }
    if (pd.bisphosphonates) {
      treatmentParts.push(
        `• + Bisphosphonates: ${pd.bisphosphonates.additionalBenefit} → ${pd.bisphosphonates.overallSurvival}%`,
      );
    }
  }

  updates["treatmentSuggestion"] = treatmentParts.join("\n");

  // Merge updates with current form data instead of replacing
  setFormData((prevData) => ({ ...prevData, ...updates }));
};


// Helper function to render form field based on type
const renderFormField = (
  fieldData: any,
  value: string,
  onChange: (value: string) => void,
  category?: string,
) => {
  const isReadOnly = ["outcome", "treatment", "predictData"].includes(
    category || "",
  );

  if (fieldData.type === FieldType.DROPDOWN && fieldData.values?.length > 0) {
    return (
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">
          {fieldData.name}
        </label>
        <select
          value={value}
          onChange={(e) => !isReadOnly && onChange(e.target.value)}
          disabled={isReadOnly}
          className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
        >
          <option value="">Select...</option>
          {fieldData.values.map((opt: string) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>
    );
  }

  const isNumber =
    fieldData.name.toLowerCase().includes("age") ||
    fieldData.name.toLowerCase().includes("size");
  const isMultiline = category === "outcome";

  if (isMultiline) {
    return (
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">
          {fieldData.name}
        </label>
        <textarea
          value={value}
          readOnly={isReadOnly}
          rows={3}
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 resize-none"
          onChange={(e) => !isReadOnly && onChange(e.target.value)}
        />
      </div>
    );
  }

  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {fieldData.name}
      </label>
      <input
        type={isNumber ? "number" : "text"}
        value={value}
        readOnly={isReadOnly}
        onChange={(e) => !isReadOnly && onChange(e.target.value)}
        className="w-full h-9 border border-slate-200 rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 read-only:bg-slate-50 read-only:text-slate-400"
      />
    </div>
  );
};

// Treatment Table Component
const TreatmentTable: React.FC<{
  formData: FormData;
  recommendations?: TreatmentRecommendations;
}> = ({ formData }) => {
  const getBadgeClass = (rec: string) => {
    const lower = rec.toLowerCase();
    if (
      lower.includes("recommended") ||
      lower.includes("required") ||
      lower.includes("refer") ||
      lower.includes("years")
    )
      return "bg-green-100 text-green-700";
    if (lower.includes("not recommended") || lower.includes("not required"))
      return "bg-red-100 text-red-700";
    if (lower.includes("consider")) return "bg-amber-100 text-amber-700";
    return "bg-slate-100 text-slate-600";
  };

  const getIcon = (rec: string) => {
    const lower = rec.toLowerCase();
    if (
      lower.includes("recommended") ||
      lower.includes("required") ||
      lower.includes("refer") ||
      lower.includes("years")
    )
      return "✓";
    if (lower.includes("not recommended") || lower.includes("not required"))
      return "✗";
    if (lower.includes("consider")) return "⚠";
    return "?";
  };

  const treatmentFields = [
    { key: "staging", label: "Staging" },
    { key: "chemotherapy", label: "Chemotherapy" },
    { key: "endocrineTherapy", label: "Endocrine Therapy" },
    { key: "radiotherapy", label: "Radiotherapy" },
    { key: "genetics", label: "Genetics" },
    { key: "bisphosphonates", label: "Bisphosphonates" },
    { key: "echo", label: "ECHO" },
    { key: "dexa", label: "DEXA" },
    { key: "duration", label: "Duration" },
  ];

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-blue-600 text-white">
          <tr>
            <th className="px-4 py-3 text-left font-semibold">
              Treatment Type
            </th>
            <th className="px-4 py-3 text-left font-semibold">
              Recommendation
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {treatmentFields.map((f) => {
            const rec = formData[f.key] || "No Recommendation";
            return (
              <tr key={f.key} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-medium text-slate-700">
                  {f.label}
                </td>
                <td className="px-4 py-2.5">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${getBadgeClass(rec)}`}
                  >
                    {getIcon(rec)} {rec}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// PREDICT Data Table Component
const PredictDataTable: React.FC<{
  formData: FormData;
  recommendations?: TreatmentRecommendations;
}> = ({ recommendations }) => {
  const [selectedYears, setSelectedYears] = useState(10);
  const [showRanges, setShowRanges] = useState(true);

  const predictData = recommendations?.predictData;

  const formatBenefit = (ab: string) => {
    if (!showRanges && ab !== "-") {
      const m = ab.match(/^(\d+\.?\d*)%/);
      return m ? `${m[1]}%` : ab;
    }
    return ab;
  };

  if (!predictData) {
    return (
      <div className="text-center py-8 text-slate-400 text-sm">
        Run analysis to see PREDICT data
      </div>
    );
  }

  const treatments = [
    {
      name: "Surgery only",
      benefit: "-",
      survival: predictData.surgeryOnly.overallSurvival,
    },
    predictData.hormoneTherapy && {
      name: "+ Hormone therapy",
      benefit: formatBenefit(predictData.hormoneTherapy.additionalBenefit),
      survival: predictData.hormoneTherapy.overallSurvival,
    },
    predictData.chemotherapy && {
      name: "+ Chemotherapy",
      benefit: formatBenefit(predictData.chemotherapy.additionalBenefit),
      survival: predictData.chemotherapy.overallSurvival,
    },
    predictData.trastuzumab && {
      name: "+ Trastuzumab",
      benefit: formatBenefit(predictData.trastuzumab.additionalBenefit),
      survival: predictData.trastuzumab.overallSurvival,
    },
    predictData.bisphosphonates && {
      name: "+ Bisphosphonates",
      benefit: formatBenefit(predictData.bisphosphonates.additionalBenefit),
      survival: predictData.bisphosphonates.overallSurvival,
    },
  ].filter(Boolean) as { name: string; benefit: string; survival: number }[];

  return (
    <div>
      <div className="mb-3">
        <p className="text-xs text-slate-500 mb-2">Years since surgery:</p>
        <div className="flex gap-1">
          {[5, 10, 15].map((y) => (
            <button
              key={y}
              onClick={() => setSelectedYears(y)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${selectedYears === y ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            >
              {y}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-hidden rounded-lg border border-slate-200 mb-3">
        <table className="w-full text-sm">
          <thead className="bg-blue-600 text-white">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Treatment</th>
              <th className="px-4 py-3 text-center font-semibold">Benefit</th>
              <th className="px-4 py-3 text-center font-semibold">
                Survival %
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {treatments.map((t, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-medium text-slate-700">
                  {t.name}
                </td>
                <td className="px-4 py-2.5 text-center text-slate-600">
                  {t.benefit}
                </td>
                <td className="px-4 py-2.5 text-center font-bold text-blue-700">
                  {t.survival}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-slate-500">Show ranges?</span>
        <button
          onClick={() => setShowRanges(true)}
          className={`px-2 py-1 rounded text-xs ${showRanges ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}
        >
          Yes
        </button>
        <button
          onClick={() => setShowRanges(false)}
          className={`px-2 py-1 rounded text-xs ${!showRanges ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}
        >
          No
        </button>
      </div>
    </div>
  );
};

const AssessmentFormEnhanced: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [progress, setProgress] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Calculate progress based on filled fields (excluding read-only fields)
  useEffect(() => {
    const allFieldNames = getAllFieldNames();

    // Get read-only field names (outcome, treatment, predictData)
    const outcomeFields = getFieldsByCategory("outcome");
    const treatmentFields = getFieldsByCategory("treatment");
    const predictDataFields = getFieldsByCategory("predictData");

    const readOnlyFieldNames = [
      ...Object.keys(outcomeFields),
      ...Object.keys(treatmentFields),
      ...Object.keys(predictDataFields),
    ];

    // Filter out read-only fields and excluded fields (name, nhs) from progress calculation
    const excludedFieldNames = ["name", "nhs"];
    const progressFieldNames = allFieldNames.filter(
      (fieldName) =>
        !readOnlyFieldNames.includes(fieldName) &&
        !excludedFieldNames.includes(fieldName),
    );
    const totalFields = progressFieldNames.length;
    const filledFields = progressFieldNames.filter((fieldName) => {
      const value = formData[fieldName];
      return value && value.trim() !== "";
    }).length;
    setProgress(totalFields > 0 ? (filledFields / totalFields) * 100 : 0);
  }, [formData]);

  // State to track if formulas have been executed
  const [formulasExecuted, setFormulasExecuted] = useState(false);
  const [recommendations, setRecommendations] =
    useState<TreatmentRecommendations | null>(null);

  // Populate form from URL parameters
  useEffect(() => {
    const urlData: Partial<FormData> = {};

    // Map URL parameters to form fields
    searchParams.forEach((value, key) => {
      urlData[key] = value;
    });

    if (Object.keys(urlData).length > 0) {
      console.log("🔗 Populating form from URL parameters:", urlData);
      setFormData((prev) => ({ ...prev, ...urlData }));
    }
  }, [searchParams]);

  // Manual formula execution function
  const executeFormulas = useCallback(() => {
    try {
      // Map form data to patient data interface
      const patientData = mapFormDataToPatientData(formData);

      // Run comprehensive recommendations
      const newRecommendations =
        MDTAidFormulas.getComprehensiveRecommendations(patientData);

      // Store recommendations for PREDICT table
      setRecommendations(newRecommendations);

      // Populate output fields with results
      populateOutputFields(formData, newRecommendations, setFormData);

      // Mark formulas as executed
      setFormulasExecuted(true);

      // Show notification that formulas have been executed
      setSnackbar({
        open: true,
        message: `✅ MDT formulas executed successfully! Analysis complete.`,
        severity: "success",
      });

      console.log("MDT Formulas executed manually:", {
        progress: progress.toFixed(1) + "%",
        patientData,
        recommendations: newRecommendations,
      });
    } catch (error) {
      console.error("Error executing MDT formulas:", error);
      setSnackbar({
        open: true,
        message:
          "❌ Error executing MDT formulas. Please check your input data.",
        severity: "error",
      });
    }
  }, [formData, progress]);

  // Auto-save to URL query parameters
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(formData).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params.toString()}`,
    );
  }, [formData]);


  const handleInputChange = useCallback((field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Show save notification
    setSnackbar({
      open: true,
      message: "Changes saved automatically",
      severity: "success",
    });
  }, []);

  const handleSaveDraft = () => {
    saveAssessment(formData);

    setSnackbar({
      open: true,
      message: "Assessment saved to history",
      severity: "success",
    });
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setFormulasExecuted(false);
    setRecommendations(null);
    setSnackbar({
      open: true,
      message: "Form reset successfully",
      severity: "success",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-16 z-30">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">MDT Assessment</h1>
          <p className="text-sm text-slate-500">
            Complete patient assessment for breast cancer risk prediction
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-2">
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-2 bg-blue-600 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span
            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${progress === 100 ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}
          >
            {Math.round(progress)}%
          </span>
        </div>
      </div>

      {/* 3-column layout */}
      <div
        className="flex-1 grid grid-cols-1 lg:grid-cols-[250px_1fr_300px] gap-0"
        style={{ height: "calc(100vh - 130px)" }}
      >
        {/* Left: Case Log */}
        <div className="hidden lg:block border-r border-slate-200 overflow-hidden">
          <CaseLogPanel
            formData={formData}
            onSave={handleSaveDraft}
            onReset={handleReset}
          />
        </div>

        {/* Center: Form */}
        <div className="overflow-y-auto p-6 space-y-6">
          {/* General Details */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faUser} className="text-blue-600" />{" "}
              General Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(
                getFieldsByCategory(
                  "generalDetails" as keyof MDTAidProperProperties,
                ),
              )
                .filter(([k]) => k !== "name" && k !== "nhs")
                .map(([key, fd]) => (
                  <div key={key}>
                    {renderFormField(
                      fd,
                      formData[key] || "",
                      (v) => handleInputChange(key, v),
                      "generalDetails",
                    )}
                  </div>
                ))}
            </div>
          </div>

          {/* Pathology */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faStethoscope} className="text-blue-600" />{" "}
              Pathology
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(
                getFieldsByCategory(
                  "pathology" as keyof MDTAidProperProperties,
                ),
              ).map(([key, fd]) => (
                <div key={key}>
                  {renderFormField(
                    fd,
                    formData[key] || "",
                    (v) => handleInputChange(key, v),
                    "pathology",
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Analyse Button */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
            <h2 className="text-base font-semibold text-slate-900 mb-1 flex items-center justify-center gap-2">
              <FontAwesomeIcon icon={faCalculator} className="text-blue-600" />{" "}
              Clinical Decision Analysis
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              Execute MDT formulas and generate treatment recommendations
            </p>
            <button
              onClick={executeFormulas}
              disabled={progress < 80 || formulasExecuted}
              className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {formulasExecuted ? "Analysis Complete ✓" : "Analyse Patient"}
            </button>
            <p className="text-xs text-slate-400 mt-2">
              {progress < 80
                ? `Complete ${Math.ceil(80 - progress)}% more fields to enable (${progress.toFixed(1)}% done)`
                : formulasExecuted
                  ? "Analysis complete. View results below."
                  : "Ready for analysis!"}
            </p>
          </div>

          {/* PREDICT Data */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faChartLine} className="text-blue-600" />{" "}
              PREDICT Data
            </h2>
            <PredictDataTable
              formData={formData}
              recommendations={recommendations || undefined}
            />
          </div>

          {/* Treatment */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faPills} className="text-blue-600" />{" "}
              Treatment
            </h2>
            <TreatmentTable
              formData={formData}
              recommendations={recommendations || undefined}
            />
          </div>

          {/* Outcome */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FontAwesomeIcon
                icon={faClipboardCheck}
                className="text-blue-600"
              />{" "}
              Outcome
            </h2>
            <div className="space-y-4">
              {Object.entries(
                getFieldsByCategory("outcome" as keyof MDTAidProperProperties),
              ).map(([key, fd]) => (
                <div key={key}>
                  {renderFormField(
                    fd,
                    formData[key] || "",
                    (v) => handleInputChange(key, v),
                    "outcome",
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Imaging + Summary */}
        <div className="hidden lg:flex flex-col border-l border-slate-200 overflow-hidden">
          <ImagingPanel
            value={formData["imaging"] || ""}
            onChange={(v) => handleInputChange("imaging", v)}
          />
          <CaseSummaryPanel
            formData={formData}
            recommendations={recommendations}
          />
        </div>
      </div>

      {/* Toast notification */}
      {snackbar.open && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-xl shadow-lg text-sm font-medium z-50 transition-all ${
            snackbar.severity === "success"
              ? "bg-green-600 text-white"
              : snackbar.severity === "error"
                ? "bg-red-600 text-white"
                : "bg-blue-600 text-white"
          }`}
        >
          {snackbar.message}
        </div>
      )}
    </div>
  );
};

export default AssessmentFormEnhanced;
